import React, { useState, useRef, useEffect } from 'react';
import LoadingSpinner from '../common/LoadingSpinner';
import containerAPI from '../../services/containerAPI';
import { validateContainerNumber, calculateCheckDigit } from '../../utils/ContainerNumberValidator';

const ContainerForm = ({ onContainerCreated }) => {
    const [portErrors, setPortErrors] = useState({ portOfLoading: '', portOfDischarge: '' });
    const [isUpdateMode, setIsUpdateMode] = useState(false);
    const [formData, setFormData] = useState({
        containerNumber: '',
        bookingNumber: '',
        shippingLine: '',
        size: '20',
        type: 'GP',
        isoType: '',
        shipperName: '',
        consigneeName: '',
        portOfLoading: '',
        portOfDischarge: '',
        vesselName: '',
        voyageNumber: '',
        eta: '',
        etd: '',
        freeDays: ''
    });

    // Helper to ensure date string is yyyy-MM-dd
    const toDateInputValue = (val) => {
        if (!val) return '';
        if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
        if (/^\d{4}-\d{2}-\d{2}T/.test(val)) return val.slice(0, 10);
        return '';
    };
    const [containerStatusCheck, setContainerStatusCheck] = useState({ loading: false, exists: null, booked: null, error: null });
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    const [ports, setPorts] = useState([]);
    const [portLoadingSearch, setPortLoadingSearch] = useState('');
    const [portDischargeSearch, setPortDischargeSearch] = useState('');

    useEffect(() => {
        containerAPI.getPorts()
            .then(res => {
                if (Array.isArray(res.data)) {
                    setPorts(res.data);
                }
            })
            .catch(() => setPorts([]));
    }, []);

    // Inject styles for centering the button (React best practice)
    useEffect(() => {
        if (typeof window !== 'undefined' && !document.head.querySelector('style[data-container-form]')) {
            const formStyle = document.createElement('style');
            formStyle.innerHTML = `
                .form-actions {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    margin-top: 1.5rem;
                }
            `;
            formStyle.setAttribute('data-container-form', '');
            document.head.appendChild(formStyle);
        }
    }, []);

    // Use ports as portOptions for datalist
    const portOptions = ports;

    // Generic handleChange for most fields
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: (name === 'etd' || name === 'eta') ? toDateInputValue(value) : value
        }));

        // If containerNumber is changed, check existence and status
        if (name === 'containerNumber' && value.length >= 7) {
            setContainerStatusCheck({ loading: true, exists: null, booked: null, error: null });
            containerAPI.getByNumber(value)
                .then(res => {
                    const data = res.data && res.data.container ? res.data.container : res.data;
                    if (data && typeof data === 'object' && data.containerNumber === value) {
                        setContainerStatusCheck({
                            loading: false,
                            exists: true,
                            booked: (data.status === 'BOOKED' || data.currentStatus === 'BOOKED'),
                            error: null
                        });
                        // Populate form fields with container details
                        setFormData({
                            containerNumber: data.containerNumber || '',
                            bookingNumber: data.bookingNumber || '',
                            shippingLine: data.shippingLine || '',
                            size: data.size || '20',
                            type: data.type || 'GP',
                            isoType: data.isoType || '',
                            shipperName: data.shipperName || '',
                            consigneeName: data.consigneeName || '',
                            portOfLoading: data.portOfLoading || '',
                            portOfDischarge: data.portOfDischarge || '',
                            vesselName: data.vesselName || '',
                            voyageNumber: data.voyageNumber || '',
                            eta: toDateInputValue(data.eta) || '',
                            etd: toDateInputValue(data.etd) || '',
                            freeDays: data.freeDays || ''
                        });
                        setIsUpdateMode(true);
                    } else {
                        setContainerStatusCheck({ loading: false, exists: false, booked: false, error: null });
                        setIsUpdateMode(false);
                    }
                })
                .catch(err => {
                    setContainerStatusCheck({ loading: false, exists: false, booked: false, error: err?.response?.data?.message || 'Not found' });
                    setIsUpdateMode(false);
                });
        }
    };

    // Custom handleChange for port fields (if you want extra logic)
    const handlePortChange = (e) => {
        const { name, value } = e.target;
        // Validate against portOptions
        const validPort = portOptions.some(port => port.portCode === value);
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setPortErrors(prev => ({
            ...prev,
            [name]: validPort || value === '' ? '' : 'Invalid port code. Please select a port from the list.'
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateContainerNumber(formData.containerNumber)) {
            alert('Invalid container number format or check digit.');
            return;
        }
        // Validate ports before submit
        const validLoading = portOptions.some(port => port.portCode === formData.portOfLoading);
        const validDischarge = portOptions.some(port => port.portCode === formData.portOfDischarge);
        if (!validLoading || !validDischarge) {
            setPortErrors({
                portOfLoading: validLoading ? '' : 'Invalid port code. Please select a port from the list.',
                portOfDischarge: validDischarge ? '' : 'Invalid port code. Please select a port from the list.'
            });
            alert('Please select valid ports from the list for both Port of Loading and Port of Discharge.');
            return;
        }
        try {
            // Ensure etd and eta are full ISO datetime strings
            const payload = { ...formData };
            if (payload.etd && /^\d{4}-\d{2}-\d{2}$/.test(payload.etd)) {
                payload.etd = payload.etd + 'T00:00:00';
            }
            if (payload.eta && /^\d{4}-\d{2}-\d{2}$/.test(payload.eta)) {
                payload.eta = payload.eta + 'T00:00:00';
            }

            payload.portOfLoading = formData.portOfLoading;
            payload.portOfDischarge = formData.portOfDischarge;

            if (isUpdateMode) {
                await containerAPI.update(formData.containerNumber, payload);
                alert('Container updated successfully!');
            } else {
                await containerAPI.create(payload);
                alert('Container created successfully!');
            }
            onContainerCreated();
            setFormData({
                containerNumber: '',
                bookingNumber: '',
                shippingLine: '',
                size: '20',
                type: 'GP',
                isoType: '',
                shipperName: '',
                consigneeName: '',
                portOfLoading: '',
                portOfDischarge: '',
                vesselName: '',
                voyageNumber: '',
                eta: '',
                etd: '',
                freeDays: ''
            });
            setIsUpdateMode(false);
        } catch (error) {
            const errorMessage = error.response?.data?.message || 
                               error.response?.data?.error || 
                               (isUpdateMode ? 'Failed to update container. Please try again.' : 'Failed to create container. Please try again.');
            alert(errorMessage);
        }
    };

    const handleBulkUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            await containerAPI.bulkUpload(formData);
            alert('Bulk upload successful!');
        } catch (error) {
            const errorMessage = error.response?.data?.message ||
                error.response?.data?.error ||
                error.message ||
                'Failed to upload containers. Please try again.';
            alert('Bulk upload failed: ' + errorMessage);

            // If token expired, redirect to login
            if (error.response?.status === 401 && error.response?.data?.error?.includes('JWT expired')) {
                sessionStorage.clear();
                window.location.href = '/login';
            }
        } finally {
            setUploading(false);
            // Reset file input so user can re-upload same file
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    return (
        <div className="container-form">
            {uploading && <LoadingSpinner text="Uploading containers..." />}
            <h3>Add New Container</h3>
            <form onSubmit={handleSubmit}>
                <div className="form-row">
                    <div className="form-group">
                        <label>Container Number *</label>
                        <input
                            type="text"
                            name="containerNumber"
                            value={formData.containerNumber}
                            onChange={handleChange}
                            required
                            placeholder="e.g., CMAU1234567"
                        />
                        {/* Status check feedback */}
                        {containerStatusCheck.loading && <span style={{ color: '#888', marginLeft: 8 }}>Checking status...</span>}
                        {containerStatusCheck.exists === false && <span style={{ color: 'red', marginLeft: 8 }}>Container not found.</span>}
                        {containerStatusCheck.exists && !containerStatusCheck.booked && <span style={{ color: 'orange', marginLeft: 8 }}>Container exists but not BOOKED.</span>}
                        {containerStatusCheck.exists && containerStatusCheck.booked && <span style={{ color: 'green', marginLeft: 8 }}>Container exists and is BOOKED.</span>}
                        {containerStatusCheck.error && <span style={{ color: 'red', marginLeft: 8 }}>{containerStatusCheck.error}</span>}
                    </div>
                    <div className="form-group">
                        <label>Booking Number *</label>
                        <input
                            type="text"
                            name="bookingNumber"
                            value={formData.bookingNumber}
                            onChange={handleChange}
                            required
                            placeholder="e.g., SHIP-6482"
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>Carrier Source *</label>
                        <input
                            type="text"
                            name="shippingLine"
                            value={formData.shippingLine}
                            onChange={handleChange}
                            required
                            placeholder="e.g., CMA CGM"
                        />
                    </div>
                    <div className="form-group">
                        <label>Container Size *</label>
                        <select
                            name="size"
                            value={formData.size}
                            onChange={handleChange}
                            required
                        >
                            <option value="20">20 ft</option>
                            <option value="40">40 ft</option>
                            <option value="45">45 ft</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Container Type *</label>
                        <select
                            name="type"
                            value={formData.type}
                            onChange={handleChange}
                            required
                        >
                            <option value="GP">GP: General Purpose (standard dry container)</option>
                            <option value="HC">HC: High Cube (taller than standard)</option>
                            <option value="RF">RF: Reefer (refrigerated)</option>
                            <option value="RH">RH: Reefer High Cube</option>
                            <option value="OT">OT: Open Top (for oversized cargo)</option>
                            <option value="FR">FR: Flat Rack (for heavy or oversized cargo)</option>
                            <option value="DD">DD: Double Door</option>
                            <option value="BK">BK: Bulk Container</option>
                            <option value="PW">PW: Pallet Wide</option>
                            <option value="SWAP">SWAP: Swap body (specialized)</option>
                            <option value="ROLL">ROLL: Roll trailer (specialized)</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label>ISO Type Code</label>
                        <select
                            name="isoType"
                            value={formData.isoType}
                            onChange={handleChange}
                        >
                            <option value="">Select ISO Type</option>
                            <option value="22G1">22G1: 20' GP (General Purpose)</option>
                            <option value="42G1">42G1: 40' GP (General Purpose)</option>
                            <option value="45G1">45G1: 45' GP (General Purpose)</option>
                            <option value="22R1">22R1: 20' RF (Reefer)</option>
                            <option value="45R1">45R1: 45' RF (Reefer)</option>
                            <option value="42R1">42R1: 40' RF (Reefer)</option>
                            <option value="22U6">22U6: 20' OT (Open Top)</option>
                            <option value="42U6">42U6: 40' OT (Open Top)</option>
                            <option value="22P3">22P3: 20' PW (Pallet Wide)</option>
                            <option value="42P3">42P3: 40' PW (Pallet Wide)</option>
                            <option value="22T5">22T5: 20' Tank</option>
                            <option value="42T5">42T5: 40' Tank</option>
                            <option value="22U1">22U1: 20' FR (Flat Rack)</option>
                            <option value="42U1">42U1: 40' FR (Flat Rack)</option>
                            <option value="22B0">22B0: 20' BK (Bulk)</option>
                            <option value="42B0">42B0: 40' BK (Bulk)</option>
                            <option value="22D1">22D1: 20' DD (Double Door)</option>
                            <option value="42D1">42D1: 40' DD (Double Door)</option>
                            <option value="45G1">45G1: 45' HC (High Cube)</option>
                            <option value="42G1">42G1: 40' HC (High Cube)</option>
                            <option value="45R1">45R1: 45' RH (Reefer High Cube)</option>
                            <option value="42R1">42R1: 40' RH (Reefer High Cube)</option>
                        </select>
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>Port of Loading</label>
                        <input
                            type="text"
                            name="portOfLoading"
                            value={formData.portOfLoading}
                            onChange={handlePortChange}
                            list="port-loading-list"
                            placeholder="Type or select port..."
                            autoComplete="off"
                        />
                        <datalist id="port-loading-list">
                            {portOptions.map(port => (
                                <option key={port.portId} value={port.portCode}>
                                    {port.portName} ({port.portCode})
                                </option>
                            ))}
                        </datalist>
                        {portErrors.portOfLoading && <span style={{ color: 'red', fontSize: '0.9em' }}>{portErrors.portOfLoading}</span>}
                    </div>
                    <div className="form-group">
                        <label>Final Port of Discharge</label>
                        <input
                            type="text"
                            name="portOfDischarge"
                            value={formData.portOfDischarge}
                            onChange={handlePortChange}
                            list="port-discharge-list"
                            placeholder="Type or select port..."
                            autoComplete="off"
                        />
                        <datalist id="port-discharge-list">
                            {portOptions.map(port => (
                                <option key={port.portId} value={port.portCode}>
                                    {port.portName} ({port.portCode})
                                </option>
                            ))}
                        </datalist>
                        {portErrors.portOfDischarge && <span style={{ color: 'red', fontSize: '0.9em' }}>{portErrors.portOfDischarge}</span>}
                    </div>
                    <div className="form-group">
                        <label>Free Days</label>
                        <input
                            type="text"
                            name="freeDays"
                            value={formData.freeDays}
                            onChange={handleChange}
                            placeholder="e.g., 5"
                        />
                    </div>
                </div>
                <div className="form-row">
                    <div className="form-group">
                        <label>Vessel Name</label>
                        <input
                            type="text"
                            name="vesselName"
                            value={formData.vesselName}
                            onChange={handleChange}
                            placeholder="e.g., Ever Given"
                        />
                    </div>
                    <div className="form-group">
                        <label>Voyage Number</label>
                        <input
                            type="text"
                            name="voyageNumber"
                            value={formData.voyageNumber}
                            onChange={handleChange}
                            placeholder="e.g., 001W"
                        />
                    </div>
                    <div className="form-group">
                        <label>ETD</label>
                        <input
                            type="date"
                            name="etd"
                            value={toDateInputValue(formData.etd)}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="form-group">
                        <label>ETA </label>
                        <input
                            type="date"
                            name="eta"
                            value={toDateInputValue(formData.eta)}
                            onChange={handleChange}
                        />
                    </div>
                </div>
                <div className="form-actions" style={{ gap: '1rem' }}>
                    <button type="submit" className="btn-primary">{isUpdateMode ? 'Update Container' : 'Create Container'}</button>
                    <button
                        type="button"
                        className="btn-primary btn-green"
                        style={{ marginLeft: '1rem', backgroundColor: '#28a745', borderColor: '#28a745', color: '#fff' }}
                        onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    >
                        Bulk Container Upload
                    </button>
                    <input
                        id="bulk-upload"
                        type="file"
                        accept=".xlsx,.xls"
                        style={{ display: 'none' }}
                        ref={fileInputRef}
                        onChange={handleBulkUpload}
                    />
                </div>
            </form>
        </div>
    );
};

export default ContainerForm;